import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
    Star,
    MessageCircle,
    Phone,
    Video,
    Play,
    BadgeCheck,
    WalletIcon,
    CreditCard,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../state/store";
import { CallModal } from "./ui/callmodal";
import { readingLine, usdToLocalLabel } from "../utilities/currency";
import { parseUntilObject, getAgentDisplayPicUrl } from "../utilities/conversions";



export default function Agents({
    baseUrl,
    T,
    currentUser,
    onLoginClick,
    onLoginSuccess,
}: {
    baseUrl: string;
    T: any;
    currentUser: any;
    onLoginClick: () => void;
    onLoginSuccess: () => void;
}) {
    const client = useSelector((state: RootState) => state.client.client);
    // --- API functions ---
    const fetchAgents = async (baseUrl: string) => {
        if (client) {
            const response = await axios.get(baseUrl + "/getAgents", { params: { client_uid: client?.uid } });
            return response.data.data;
        }
    };

    const fetchStatuses = async (baseUrl: string) => {
        if (client) {
            const response = await axios.get(baseUrl + "/getAgentsStatuses", { params: { client_uid: client?.uid } });
            return response.data.data; // [{ uid, call_status }]
        }
    };
    const fetchCustomer = async () => {
        const response = await axios.get(baseUrl + "/getCustomerByUID", {
            params: { token: currentUser.token },
        });
        if (response.data && response.data.status === "OK") {
            return response.data.data.uid;
        }
        throw new Error("Failed to fetch customer");
    };
    const { data: customer_uid } = useQuery({
        queryKey: ["customer", currentUser?.token],
        queryFn: fetchCustomer,
        enabled: !!currentUser, // only run if logged in
    });
    const default_configs = client ? JSON.parse(client.default_configs) : "";
    const footer_settings = client ? JSON.parse(client.footer_settings) : "";

    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
    const [minutePrice, setMinutePrice] = useState<string | null>(null);
    const [callStartupFee, setCallStartupFee] = useState<string | null>(null);
    const [showCallModal, setShowCallModal] = useState(false);
    const [webSocketConn, setWebSocketConn] = useState<WebSocket | null>(null);

    const [vippsAuthenticate, setVippsAuthenticate] = useState(false);
    const [walletAuthenticate, setWalletAuthenticate] = useState(false);
    const [stripeAuthenticate, setStripeAuthenticate] = useState(false);
    const [responseAgent, setResponseAgent] = useState(false);
    const [waitingAgentCheck, setWaitingAgentCheck] = useState(false);
    const [callResponseObj, setCallResponseObj] = useState<any>(null);
    const [waitingCallback, setWaitingCallback] = useState(false);
    const [waitingCallbackInfo, setWaitingCallbackInfo] = useState<any>(null);

    // --- React Query: fetch agents once ---
    const { data: agents } = useQuery({
        queryKey: ["agents"],
        queryFn: () => fetchAgents(baseUrl),
        staleTime: Infinity,
        enabled: !!client, // only run if client is available
    });

    // --- React Query: poll only statuses ---
    const { data: statuses } = useQuery({
        queryKey: ["agent-statuses"],
        queryFn: () => fetchStatuses(baseUrl),
        refetchInterval: 5000, // 5 sec poll
        enabled: !!client,
    });



    //cancel convert call to callback
    const handleCancelConvertCallToCallback = (call_id: string) => {
        setShowCallModal(false);
        var formData = new FormData();
        formData.append('call_id', call_id);
        axios.post(baseUrl + "/cancelWebCallPayment", formData);
    }

    //convert call to callback
    const handleConvertCallToCallback = async (call_id: string) => {
        var formData = new FormData();
        formData.append('call_id', call_id);
        const response = await axios.post(baseUrl + "/updateWebCallToCallback", formData);

        if (response.data && response.data.status === "OK") {
            var data = {
                call_history_id: call_id,
                about: "callback_initated_call",
                channel: "common_channel",
                type: 'audio_call',
            };
            const connection = webSocketConn;
            if (connection && connection.readyState === WebSocket.OPEN) {
                connection.send(JSON.stringify(data));
            }

            setWaitingCallback(true);
            setWaitingCallbackInfo({ send_callback_email: response.data.send_callback_email });
            setCallResponseObj(null);
            setResponseAgent(false);
        }
    }

    // --- Merge full agent data with statuses ---
    const advisors = useMemo(() => {
        if (!agents) return [];

        return agents.map((agent: any) => {
            // safe copies / computed fields (avoid mutating original)
            const display_pic = getAgentDisplayPicUrl(agent.display_pic ?? null, baseUrl);

            // parse created_at -> created_year & day_month (defensive)
            let created_year = agent.created_year ?? "";
            let day_month = agent.day_month ?? "";
            if (agent.created_at) {
                try {
                    const datePart = String(agent.created_at).split(" ")[0]; // "YYYY-MM-DD"
                    const [year, month, day] = datePart.split("-");
                    if (year && month && day) {
                        created_year = year;
                        day_month = `${day}${month}`;
                    }
                } catch (e) {
                    // leave existing values if parsing fails
                }
            }

            // ensure minute price is a number
            let customer_minute_price = agent.customer_minute_price;
            if (typeof customer_minute_price === "string") {
                const parsed = parseFloat(customer_minute_price);
                customer_minute_price = isNaN(parsed) ? 0 : parsed;
            }

            // find latest status from statuses list (if present)
            const statusObj = statuses?.find((s: any) => s.uid === agent.uid);
            const call_status = statusObj?.call_status ?? agent.call_status;

            return {
                ...agent,
                display_pic,
                created_year,
                day_month,
                customer_minute_price,
                call_status,
            };
        });
    }, [agents, statuses, baseUrl]);

    function statusPill(status: string) {
        status = status?.toUpperCase();
        if (status === "OFFLINE") return "PÅLOGGET";
        if (status === "BUSY") return "OPPTATT";
        if (status === "AVAILABLE") return "LEDIG";
        return status;
    }


    useEffect(() => {
        try {
            let data: any = "";

            const wsServer = baseUrl.includes("localhost")
                ? "ws://localhost:8013/echo"
                : "wss://chamcore.com/echo";

            const connection = new WebSocket(wsServer);
            connection.onopen = () => connection.send("Connection Established!");
            connection.onmessage = function (e) {
                if (e.data !== "" && e.data !== null) {
                    data = parseUntilObject(e.data);
                }
                if (data !== "" && data.information !== "undefined" && data.information !== "") {
                    if (data.type === "video_call") {
                        if (data.information.customer_id === customer_uid && data.about === "rejected_call") {
                            setResponseAgent(true);
                            setWaitingAgentCheck(false);
                            setCallResponseObj({ title: "Anrop avvist av agent", message: "Samtalen avbrutt" });
                            // $("#in_video_call").html("Call rejected by agent<br>Call Disconnected");
                            // setTimeout(() => {
                            //     if (window.innerWidth > 550) {
                            //         $("#videoCallModal")[0].childNodes[1].style.width = "300px";
                            //     }
                            //     $("#videoCallModal").modal("toggle");
                            // }, 1500);
                        } else if (data.information.customer_id === customer_uid && data.about === "accepted_call") {
                            window.open("customer/one-to-one-video-call/" + data.call_history_id, "_blank");
                        }
                    } else if (data.type === "audio_call") {
                        if (data.information.customer_id === customer_uid && data.about === "callback_rejected_by_agent") {

                        } else if (data.information.customer_id === customer_uid && data.about === "WebCall_rejected_by_agent") {
                            setResponseAgent(true);
                            setWaitingAgentCheck(false);
                            setCallResponseObj({ title: "Beklager at agenten avviste anropet ditt.", message: "Av en eller annen grunn kunne ikke agenten ta imot samtalen din akkurat nå.", more_info: { call_history_id: data.call_history_id } });
                            // $(".WebCallWaitingModalBody").html(`<p class="col-12 text-center">We are sorry<br>The agent<br>rejected your call</p><br><br><button style="padding: 5px 15px;border-radius: 20px;" data-id="` + data.call_history_id + `" class="btn-success btnCancellConvertCallToCallback">OK</button><br><br><small class="col-12 text-center">For some reason the agent was not able to take your call right now</small><br><small class="col-12 text-center">Do you want the agent to call you back within the next X hours?</small><div class="col-12 text-center"><button data-dismiss="modal" style="padding: 5px 15px;border-radius: 20px;" data-id="` + data.call_history_id + `" class="btn-danger btnCancellConvertCallToCallback">No</button>&nbsp;&nbsp;<button style="padding: 5px 15px;border-radius: 20px;" data-id="` + data.call_history_id + `" class="btn-success btnConvertCallToCallback">Yes</button></div>`)
                        } else if (data.information.customer_id === customer_uid && data.about === "WebCall_accepted_call") {
                            setResponseAgent(false);
                            setWaitingAgentCheck(false);
                            setCallResponseObj(null);
                            setVippsAuthenticate(false);
                            setWalletAuthenticate(false);
                            setStripeAuthenticate(false);
                            setShowCallModal(false);
                            setTimeout(() => {
                                window.open("customer/one-to-one-audio-call/" + data.call_history_id, "_blank");
                                // $("#WebCallWaitingModal").modal("toggle");
                            }, 5000);
                        }
                    }
                }
            };
            setWebSocketConn(connection);

            // ✅ cleanup on unmount / re-run
            return () => {
                connection.close();
            };

        } catch (e) {
            console.log("WebSocket error:", e);
        }

    }, [customer_uid])

    const handlePhoneClick = (
        uid: string,
        minute_price: number,
        call_startup_fee: string,
        agent_call_permission: string,
    ) => {
        if (default_configs.WebRTC === "yes" && agent_call_permission === "1") {
            setSelectedAgent(uid);
            setMinutePrice(minute_price.toString());
            setCallStartupFee(call_startup_fee);
            setShowCallModal(true);
        } else {
            window.location.href = `tel:${footer_settings.footer_phone}`;
        }
    };

    return (
        <section className="py-12 px-4 max-w-7xl mx-auto">
            <div className="grid gap-6 md:grid-cols-4">
                {advisors.slice(0, 4).map((a: any, index: number) => (
                    <Card
                        key={`a-top-${index}`}
                        className="rounded-2xl shadow-md hover:shadow-lg transition overflow-hidden relative bg-gradient-to-b from-purple-50 to-white"
                    >
                        <a href={"agent/" + a.code}>
                            <img
                                src={a.display_pic ?? ""}
                                alt={a.name ?? ""}
                                className="w-full h-64 object-contain"
                            />
                        </a>
                        <div className="absolute top-3 right-3 bg-white rounded-full p-1 shadow">
                            <Play className="w-5 h-5 text-purple-700" />
                        </div>
                        <CardContent className="p-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-bold text-purple-700 flex items-center gap-2">
                                    <a href={"agent/" + a.code}>{a.name}</a>
                                    {a.is_active && (
                                        <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold bg-green-100 px-2 py-0.5 rounded-full">
                                            <BadgeCheck className="w-3 h-3" /> {T.verified}
                                        </span>
                                    )}
                                </h3>
                                <div className="flex items-center gap-1 text-yellow-500 text-sm">
                                    <Star className="w-4 h-4" /> {a.rating}
                                </div>
                            </div>
                            <p className="text-gray-600 italic text-sm">{a.skills}</p>
                            {/* <div className="mt-2 text-sm font-semibold">
                                {readingLine(Number(a.day_month ?? 0), Number(a.created_year ?? 0))}
                            </div> */}
                            <span
                                className={`inline-block mt-2 px-3 py-1 text-xs font-bold rounded-full ${a.call_status?.toUpperCase() === "AVAILABLE" ||
                                    a.call_status?.toUpperCase() === "ONLINE"
                                    ? "bg-green-500 text-white"
                                    : "bg-red-500 text-white"
                                    }`}
                            >
                                {statusPill(a.call_status)}
                            </span>
                            <div className="flex gap-4 mt-3 text-purple-600">
                                <MessageCircle className="w-5 h-5 cursor-pointer" />
                                <Phone
                                    className="w-5 h-5 cursor-pointer"
                                    onClick={() =>
                                        handlePhoneClick(
                                            a.uid,
                                            a.customer_minute_price,
                                            a.customer_call_startup_fee,
                                            a.permissions.WebRTC
                                        )
                                    }
                                />
                                {default_configs.video === "yes" && a.can_video_call === "1" ? (
                                    <Video className="w-5 h-5 cursor-pointer" />
                                ) : (
                                    ""
                                )}
                            </div>
                            <div className="mt-3 text-gray-700 text-sm flex items-center gap-2">
                                {T.from} {usdToLocalLabel(String(a.customer_minute_price))}{" "}
                                <BadgeCheck className="w-4 h-4 text-green-600" />
                            </div>
                            {/* <div className="mt-2 flex items-center gap-2">
                                <span className="text-xs font-semibold rounded-full px-2 py-1 bg-purple-200/90 text-purple-900 flex items-center gap-1">
                                    <WalletIcon className="w-3 h-3" /> Vipps MobilePay
                                </span>
                                <span className="text-xs font-semibold rounded-full px-2 py-1 bg-blue-200/90 text-blue-900 flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" /> Kort
                                </span>
                            </div> */}
                            <Button className="mt-4 w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full">
                                {T.connectNow}
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <CallModal
                show={showCallModal}
                onClose={() => setShowCallModal(false)}
                baseUrl={baseUrl}
                agent_uid={selectedAgent}
                minutePrice={minutePrice}
                currentUser={currentUser}
                callStartupFee={callStartupFee}
                webSocketConn={webSocketConn}
                responseAgent={responseAgent}
                setResponseAgent={setResponseAgent}
                waitingAgentCheck={waitingAgentCheck}
                setWaitingAgentCheck={setWaitingAgentCheck}
                vippsAuthenticate={vippsAuthenticate}
                setVippsAuthenticate={setVippsAuthenticate}
                walletAuthenticate={walletAuthenticate}
                setWalletAuthenticate={setWalletAuthenticate}
                stripeAuthenticate={stripeAuthenticate}
                setStripeAuthenticate={setStripeAuthenticate}
                callResponseObj={callResponseObj}
                setCallResponseObj={setCallResponseObj}
                handleCancelConvertCallToCallback={handleCancelConvertCallToCallback}
                handleConvertCallToCallback={handleConvertCallToCallback}
                waitingCallback={waitingCallback}
                waitingCallbackInfo={waitingCallbackInfo}
                onLoginSuccess={onLoginSuccess}
            />
        </section>
    );
}
